import { useState } from 'react';
import { moscowMonth } from '../../api/payroll';
import {
  type EmployeeKpi,
  formatIsoDate,
  formatValue,
  type KpiItemInput,
  type KpiPeriod,
  useEmployeeKpi,
  useSaveKpi,
} from '../../api/records';
import type { KpiCommitment } from '../../api/regulations';
import { Icon } from '../../components/Icon';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import { SalarySummary } from './PayrollSection';

const periodLabel = (period: KpiPeriod) => `${formatIsoDate(period.from)} — ${formatIsoDate(period.to)}`;

interface DraftRow {
  key: number;
  name: string;
  unit: string;
  direction: 'up' | 'down';
  plan: string;
  fact: string;
}

interface Draft {
  isNew: boolean;
  from: string;
  to: string;
  /** Период, который правим: если даты изменили, старый период заменяется новым. */
  origin: KpiPeriod | null;
  rows: DraftRow[];
}

let nextKey = 1;
const emptyRow = (): DraftRow => ({
  key: nextKey++,
  name: '',
  unit: '',
  direction: 'up',
  plan: '',
  fact: '',
});
const numberText = (value: number | null | undefined) =>
  value === null || value === undefined ? '' : String(value);
const parseNumber = (value: string): number | null => {
  const cleaned = value.replace(/[\s ]/g, '').replace(',', '.');
  if (cleaned === '') return null;
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : Number.NaN;
};

function currentMonth(): KpiPeriod {
  const now = new Date();
  const from = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
  const to = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0));
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

function rowsFrom(data: EmployeeKpi | undefined, withFacts: boolean): DraftRow[] {
  if (!data?.items.length) return [emptyRow()];
  return data.items.map((item) => ({
    key: nextKey++,
    name: item.name,
    unit: item.unit ?? '',
    direction: item.direction,
    plan: numberText(item.plan),
    fact: withFacts ? numberText(item.fact) : '',
  }));
}

/** Проверяет черновик: строки без названия пропускаются, числа — с запятой или точкой. */
function toItems(rows: DraftRow[]): { items: KpiItemInput[]; error: string | null } {
  const items: KpiItemInput[] = [];
  for (const row of rows) {
    const name = row.name.replace(/\s+/g, ' ').trim();
    if (!name && !row.plan && !row.fact) continue;
    if (!name) return { items, error: 'У показателя нет названия' };
    const plan = parseNumber(row.plan);
    const fact = parseNumber(row.fact);
    if (plan === null || Number.isNaN(plan)) return { items, error: `«${name}»: план должен быть числом` };
    if (fact !== null && Number.isNaN(fact)) return { items, error: `«${name}»: факт должен быть числом` };
    items.push({ name, unit: row.unit.trim() || null, direction: row.direction, plan, fact });
  }
  const names = items.map((item) => item.name.toLowerCase());
  const duplicate = items.find((item, index) => names.indexOf(item.name.toLowerCase()) !== index);
  if (duplicate) return { items, error: `Показатель «${duplicate.name}» указан дважды` };
  return { items, error: null };
}

/**
 * «Оклад и KPI»: сводка за месяц выбранного периода KPI (оклад, премия за KPI, удержания, итог)
 * и показатели KPI. KPI заполняет непосредственный руководитель или администратор.
 */
export function KpiSection({
  employeeId,
  commitments = [],
}: {
  employeeId: string;
  /** Что сотрудник делает по показателю — из регламента. Встаёт под названием показателя. */
  commitments?: KpiCommitment[];
}) {
  const [period, setPeriod] = useState<KpiPeriod | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const kpi = useEmployeeKpi(employeeId, period);
  const commitmentOf = (name: string) =>
    commitments.find((item) => item.name.toLowerCase() === name.toLowerCase());
  const save = useSaveKpi(employeeId);
  const data = kpi.data;

  const startEdit = () => {
    if (!data?.period) return;
    save.reset();
    setDraft({ isNew: false, ...data.period, origin: data.period, rows: rowsFrom(data, true) });
  };
  const startNew = () => {
    save.reset();
    // Показатели прошлого периода подставляются без факта: обычно меняются только план и факт.
    setDraft({ isNew: true, ...currentMonth(), origin: null, rows: rowsFrom(data, false) });
  };
  const updateRow = (key: number, patch: Partial<DraftRow>) =>
    setDraft((current) =>
      current
        ? { ...current, rows: current.rows.map((row) => (row.key === key ? { ...row, ...patch } : row)) }
        : null,
    );
  const checked = draft ? toItems(draft.rows) : null;
  const periodError =
    draft && (!draft.from || !draft.to)
      ? 'Укажите период'
      : draft && draft.to < draft.from
        ? 'Период заканчивается раньше, чем начинается'
        : draft &&
            data?.periods.some(
              (p) =>
                p.from === draft.from &&
                p.to === draft.to &&
                !(draft.origin && p.from === draft.origin.from && p.to === draft.origin.to),
            )
          ? 'KPI за этот период уже заполнены — выберите период и нажмите «Изменить»'
          : null;
  const error = periodError ?? checked?.error ?? null;

  const submit = (items: KpiItemInput[]) => {
    if (!draft) return;
    const target = { from: draft.from, to: draft.to };
    const origin = draft.origin;
    const moved = Boolean(origin && (origin.from !== target.from || origin.to !== target.to));
    save.mutate(
      { ...target, items },
      {
        onSuccess: async () => {
          // Период сдвинули: показатели уже сохранены под новыми датами, старый период убираем.
          if (moved && origin && items.length) {
            await save.mutateAsync({ ...origin, items: [] });
          }
          setPeriod(items.length ? target : null);
          setDraft(null);
        },
      },
    );
  };

  return (
    <section id="kpi" className="panel">
      <div className="panel-header">
        <h2>Оклад и KPI</h2>
        <div className="panel-actions">
          {draft ? (
            <>
              {!draft.isNew ? (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={save.isPending}
                  onClick={() => {
                    if (window.confirm(`Удалить KPI за период ${periodLabel(draft)}?`)) submit([]);
                  }}
                >
                  <Icon name="trash" size={14} />
                  Удалить период
                </button>
              ) : null}
              <button type="button" className="btn-secondary" onClick={() => setDraft(null)}>
                Отмена
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={save.isPending || Boolean(error) || !checked?.items.length}
                onClick={() => checked && submit(checked.items)}
              >
                Сохранить
              </button>
            </>
          ) : (
            <>
              {data?.period && data.periods.length > 1 ? (
                <select
                  className="select"
                  aria-label="Период KPI"
                  value={`${data.period.from}|${data.period.to}`}
                  onChange={(event) => {
                    const [from = '', to = ''] = event.target.value.split('|');
                    setPeriod({ from, to });
                  }}
                >
                  {data.periods.map((p) => (
                    <option key={`${p.from}|${p.to}`} value={`${p.from}|${p.to}`}>
                      {periodLabel(p)}
                    </option>
                  ))}
                </select>
              ) : data?.period ? (
                <span className="reg-version">{periodLabel(data.period)}</span>
              ) : null}
              {data?.period ? (
                <span
                  className={`status ${data.total === null ? 'status-neutral' : data.total < 80 ? 'status-bad' : 'status-ok'}`}
                >
                  {data.total === null ? 'Факт не внесён' : `Итог: ${data.total} %`}
                </span>
              ) : null}
              {data?.canEdit && data.period ? (
                <>
                  <button type="button" className="btn-secondary" onClick={startEdit}>
                    <Icon name="pen" size={14} />
                    Изменить
                  </button>
                  <button type="button" className="btn-secondary" onClick={startNew}>
                    <Icon name="plus" size={14} />
                    Новый период
                  </button>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>

      {!draft ? (
        <SalarySummary employeeId={employeeId} month={data?.period?.to.slice(0, 7) ?? moscowMonth()} />
      ) : null}

      {kpi.isPending ? <LoadingState label="Загружаю KPI" /> : null}
      {kpi.isError ? <ErrorState error={kpi.error} onRetry={() => void kpi.refetch()} /> : null}

      {draft ? (
        <div className="kpi-editor">
          {draft ? (
            <div className="import-fields">
              <label className="field">
                <span>Период с</span>
                <input
                  className="input"
                  type="date"
                  value={draft.from}
                  onChange={(event) => setDraft({ ...draft, from: event.target.value })}
                />
              </label>
              <label className="field">
                <span>по</span>
                <input
                  className="input"
                  type="date"
                  value={draft.to}
                  onChange={(event) => setDraft({ ...draft, to: event.target.value })}
                />
              </label>
            </div>
          ) : null}
          <div className="table-scroll">
            <table className="kpi-edit-table">
              <thead>
                <tr>
                  <th>Показатель</th>
                  <th>Единица</th>
                  <th>Лучше</th>
                  <th className="number">План</th>
                  <th className="number">Факт</th>
                  <th>
                    <span className="visually-hidden">Удалить</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {draft.rows.map((row, index) => (
                  <tr key={row.key}>
                    <td>
                      <input
                        className="input kpi-name"
                        aria-label={`Показатель ${index + 1}`}
                        value={row.name}
                        placeholder="Например, новые договоры аренды"
                        onChange={(event) => updateRow(row.key, { name: event.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        className="input kpi-unit"
                        aria-label={`Единица ${index + 1}`}
                        value={row.unit}
                        placeholder="шт., %"
                        onChange={(event) => updateRow(row.key, { unit: event.target.value })}
                      />
                    </td>
                    <td>
                      <select
                        className="select"
                        aria-label={`Лучше ${index + 1}`}
                        value={row.direction}
                        onChange={(event) =>
                          updateRow(row.key, { direction: event.target.value as 'up' | 'down' })
                        }
                      >
                        <option value="up">больше</option>
                        <option value="down">меньше</option>
                      </select>
                    </td>
                    <td>
                      <input
                        className="input kpi-number"
                        inputMode="decimal"
                        aria-label={`План ${index + 1}`}
                        value={row.plan}
                        onChange={(event) => updateRow(row.key, { plan: event.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        className="input kpi-number"
                        inputMode="decimal"
                        aria-label={`Факт ${index + 1}`}
                        value={row.fact}
                        placeholder="—"
                        onChange={(event) => updateRow(row.key, { fact: event.target.value })}
                      />
                    </td>
                    <td className="link-cell">
                      <button
                        type="button"
                        className="btn-icon ghost"
                        aria-label={`Удалить показатель ${index + 1}`}
                        onClick={() =>
                          setDraft({ ...draft, rows: draft.rows.filter((item) => item.key !== row.key) })
                        }
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="kpi-editor-foot">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDraft({ ...draft, rows: [...draft.rows, emptyRow()] })}
            >
              <Icon name="plus" size={14} />
              Добавить показатель
            </button>
            <span className="muted">Факт можно внести позже.</span>
          </div>
          {error ? <p className="text-bad">{error}</p> : null}
          {save.isError ? <ErrorState title="KPI не сохранены" error={save.error} /> : null}
        </div>
      ) : null}

      {!draft && data && !data.period ? (
        <EmptyState
          title="KPI ещё не заполнены"
          action={
            data.canEdit ? (
              <button type="button" className="btn-primary" onClick={startNew}>
                <Icon name="pen" size={14} />
                Заполнить KPI
              </button>
            ) : undefined
          }
        />
      ) : null}

      {!draft && data?.period ? (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Показатель</th>
                <th className="number">План</th>
                <th className="number">Факт</th>
                <th>Выполнение</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.name}>
                  <td>
                    <strong>{item.name}</strong>
                    {item.unit || item.direction === 'down' ? (
                      <small>
                        {[item.unit, item.direction === 'down' ? 'меньше — лучше' : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </small>
                    ) : null}
                    {commitmentOf(item.name) ? (
                      <small className="kpi-what">{commitmentOf(item.name)?.what}</small>
                    ) : null}
                  </td>
                  <td className="number">{formatValue(item.plan)}</td>
                  <td className="number">{item.fact === null ? '—' : formatValue(item.fact)}</td>
                  <td>
                    {item.completion === null ? (
                      <span className="muted">нет факта</span>
                    ) : (
                      <div className={`progress${item.completion < 80 ? ' low' : ''}`}>
                        <div className="track">
                          <i style={{ width: `${Math.min(item.completion, 100)}%` }} />
                        </div>
                        <span className="number">{item.completion} %</span>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
